import React, { useCallback, useRef } from 'react';
import SvgLink from '../components/SvgLink';
import styled, { keyframes } from 'styled-components';
import html2pdf from 'html2pdf.js';

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
`;

const StyledBackground = styled.div`
    min-height: 100vh;
    background: #0f0f23;
    padding: 100px 20px 60px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #e0dcd4;
`;

const ResumeContainer = styled.div`
    max-width: 900px;
    margin: 0 auto;
    animation: ${fadeIn} 0.6s ease-out;
`;

const ResumeHeader = styled.header`
    text-align: center;
    margin-bottom: 48px;
    padding-bottom: 32px;
    border-bottom: 1px solid rgba(244, 212, 163, 0.12);
`;

const StyledHeading = styled.h1`
    font-size: 2.4em;
    font-weight: 700;
    margin: 0 0 6px;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #f4d4a3, #e8b96e);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const Subtitle = styled.p`
    font-size: 1.05em;
    color: rgba(244, 212, 163, 0.7);
    margin: 0 0 4px;
    font-weight: 300;
    letter-spacing: 0.04em;
`;

const ContactInfo = styled.p`
    font-size: 0.85em;
    color: rgba(224, 220, 212, 0.45);
    margin: 0 0 20px;
`;

const HeaderSkills = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
    max-width: 720px;
    margin: 0 auto;
`;

const ResumeContent = styled.div`
    display: grid;
    grid-template-columns: 320px 1fr;
    gap: 48px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 32px;
    }
`;

const LeftColumn = styled.section``;

const RightColumn = styled.section``;

const SectionTitle = styled.h2`
    font-size: 0.72em;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #f4d4a3;
    margin: 0 0 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(244, 212, 163, 0.15);
`;

const ItemBlock = styled.div`
    margin-bottom: 20px;
`;

const StyledH3 = styled.h3`
    font-size: 0.95em;
    font-weight: 600;
    margin: 0 0 2px;
    color: #e8e4dc;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const StyledH4 = styled.h4`
    font-size: 0.82em;
    font-weight: 500;
    margin: 8px 0 2px;
    color: rgba(244, 212, 163, 0.65);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const YearRange = styled.span`
    font-size: 0.75em;
    font-weight: 400;
    color: rgba(224, 220, 212, 0.35);
    white-space: nowrap;
    flex-shrink: 0;
`;

const SkillTag = styled.span`
    font-size: 0.65em;
    padding: 3px 8px;
    color: rgba(244, 212, 163, 0.55);
    border-radius: 100px;
    border: 1px solid rgba(244, 212, 163, 0.15);
    transition: all 0.2s ease;
    cursor: default;

    &:hover {
        background: rgba(244, 212, 163, 0.12);
        border-color: rgba(244, 212, 163, 0.35);
        color: #f4d4a3;
    }
`;

const UserSkill = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 8px;
`;

const StyledParagraph = styled.p`
    font-size: 0.82em;
    color: rgba(224, 220, 212, 0.6);
    margin: 2px 0;
    line-height: 1.5;
`;

const StyledDescription = styled.p`
    font-size: 0.8em;
    color: rgba(224, 220, 212, 0.55);
    margin: 4px 0;
    line-height: 1.55;
`;

const StyledUl = styled.ul`
    margin: 4px 0 0;
    padding-left: 16px;
`;

const StyledLi = styled.li`
    font-size: 0.78em;
    color: rgba(224, 220, 212, 0.5);
    line-height: 1.6;
    margin-bottom: 2px;

    &::marker {
        color: rgba(244, 212, 163, 0.3);
    }
`;

const ExperienceItem = styled.div`
    margin-bottom: 28px;
    padding-left: 16px;
    border-left: 2px solid rgba(244, 212, 163, 0.1);
    transition: border-color 0.3s ease;

    &:hover {
        border-left-color: rgba(244, 212, 163, 0.35);
    }
`;

const ResumeFooter = styled.footer`
    text-align: center;
    margin-top: 48px;
    padding-top: 24px;
    border-top: 1px solid rgba(244, 212, 163, 0.08);
`;

const DownloadButton = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.82em;
    font-weight: 500;
    color: rgba(244, 212, 163, 0.5);
    text-decoration: none;
    padding: 8px 20px;
    border: 1px solid rgba(244, 212, 163, 0.15);
    border-radius: 100px;
    transition: all 0.25s ease;
    cursor: pointer;

    &:hover {
        color: #f4d4a3;
        border-color: rgba(244, 212, 163, 0.4);
        background: rgba(244, 212, 163, 0.06);
    }
`;

const P = {
    page:       { width: '794px', padding: '36px 40px', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", color: '#222', background: '#fff', boxSizing: 'border-box' },
    header:     { textAlign: 'center', marginBottom: '20px', paddingBottom: '14px', borderBottom: '1.5px solid #e0e0e0' },
    name:       { fontSize: '22px', fontWeight: 700, margin: '0 0 3px', color: '#1a1a1a', letterSpacing: '-0.01em' },
    subtitle:   { fontSize: '11px', color: '#6b5b3e', margin: '0 0 2px', fontWeight: 400, letterSpacing: '0.04em' },
    email:      { fontSize: '9.5px', color: '#888', margin: '0 0 10px' },
    skillWrap:  { display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '4px' },
    skill:      { fontSize: '7.5px', padding: '2px 7px', border: '1px solid #ccc', borderRadius: '50px', color: '#555' },
    cols:       { display: 'flex', gap: '32px' },
    left:       { width: '260px', flexShrink: 0 },
    right:      { flex: 1 },
    section:    { fontSize: '8.5px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6b5b3e', margin: '14px 0 8px', paddingBottom: '4px', borderBottom: '1px solid #e8e8e8' },
    itemTitle:  { fontSize: '10.5px', fontWeight: 600, margin: '0 0 1px', color: '#1a1a1a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    year:       { fontSize: '8px', fontWeight: 400, color: '#999', whiteSpace: 'nowrap', flexShrink: 0 },
    desc:       { fontSize: '9px', color: '#555', margin: '2px 0', lineHeight: '1.5' },
    projTitle:  { fontSize: '9.5px', fontWeight: 500, margin: '5px 0 1px', color: '#6b5b3e' },
    ul:         { margin: '2px 0 0', paddingLeft: '14px' },
    li:         { fontSize: '8.5px', color: '#555', lineHeight: '1.55', marginBottom: '1px' },
    expBlock:   { marginBottom: '14px', paddingLeft: '10px', borderLeft: '2px solid #d4c4a0' },
    itemBlock:  { marginBottom: '12px' },
    skillRow:   { display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '4px' },
    skillSmall: { fontSize: '7px', padding: '1.5px 6px', border: '1px solid #d0d0d0', borderRadius: '50px', color: '#777' },
};

const PrintSkills = ({ items }) => (
    <div style={P.skillRow}>
        {items.map(s => <span key={s} style={P.skillSmall}>{s}</span>)}
    </div>
);

const PrintResume = React.forwardRef((_, ref) => (
    <div ref={ref} style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <div style={P.page}>
            <div style={P.header}>
                <div style={P.name}>Da Yo Tseng 曾大祐</div>
                <div style={P.subtitle}>Full Stack & Game Developer</div>
                <div style={P.email}>alan25799@gmail.com</div>
                <div style={P.skillWrap}>
                    {['C#','Java','JavaScript','Go','Python','Ruby','Ruby on Rails','Unity','React','Firebase','AWS','Django','Flask','PostgreSQL','TypeScript','Next.js'].map(s =>
                        <span key={s} style={P.skill}>{s}</span>
                    )}
                </div>
            </div>

            <div style={P.cols}>
                <div style={P.left}>
                    <div style={P.section}>Education</div>
                    <div style={P.itemBlock}>
                        <div style={P.itemTitle}>National Taiwan University <span style={P.year}>2014 – 2016</span></div>
                        <div style={P.desc}>B.S. in Computer Science and Information Engineering</div>
                        <div style={P.desc}>Coursework:</div>
                        <ul style={P.ul}>
                            <li style={P.li}>Web Design</li>
                            <li style={P.li}>Computer Graphics</li>
                            <li style={P.li}>Data Structures & Algorithms</li>
                            <li style={P.li}>Database Management Systems</li>
                            <li style={P.li}>Game Design</li>
                        </ul>
                    </div>
                    <div style={P.itemBlock}>
                        <div style={P.itemTitle}>National Taiwan University <span style={P.year}>2011 – 2014</span></div>
                        <div style={P.desc}>Minor in Civil Engineering</div>
                        <ul style={P.ul}>
                            <li style={P.li}>Engineering Graphics</li>
                            <li style={P.li}>Application of Visualization Technology in Engineering</li>
                        </ul>
                    </div>

                    <div style={P.section}>Collaborative Projects</div>
                    <div style={P.itemBlock}>
                        <div style={P.itemTitle}>Artist App Development <span style={P.year}>2018 – 2019</span></div>
                        <div style={P.desc}>Developed front-end with React.js and React Native.</div>
                        <PrintSkills items={['React.js','React Native']} />
                    </div>
                    <div style={P.itemBlock}>
                        <div style={P.itemTitle}>CareDesign <span style={P.year}>July 2017</span></div>
                        <div style={P.desc}>Implemented a machine learning feature for a caregiving startup's iOS app to suggest diagnoses based on symptoms.</div>
                        <PrintSkills items={['iOS','Swift','Flask','Heroku','Python','Keras','Sklearn']} />
                    </div>
                    <div style={P.itemBlock}>
                        <div style={P.itemTitle}>Representative Office of the MTC in Taipei</div>
                        <div style={{...P.year, textAlign: 'left'}}>2016</div>
                        <div style={P.desc}>A three-person project where I handled multi-language switching and Facebook integration using CouchCMS.</div>
                        <PrintSkills items={['CouchCMS','JavaScript','React']} />
                    </div>
                    <div style={P.itemBlock}>
                        <div style={P.itemTitle}>ParkingLotsGo <span style={P.year}>2016</span></div>
                        <div style={P.desc}>Developed a mobile application to address parking lot issues using Swift and Xcode.</div>
                        <PrintSkills items={['iOS','Xcode','Swift']} />
                    </div>
                    <div style={P.itemBlock}>
                        <div style={P.itemTitle}>NTU Water Resource Education Game <span style={P.year}>2013</span></div>
                        <div style={P.desc}>Developed educational games for a National Science Council project on water resources.</div>
                        <PrintSkills items={['Unity','C#']} />
                    </div>
                </div>

                <div style={P.right}>
                    <div style={P.section}>Work Experience</div>
                    <div style={P.expBlock}>
                        <div style={P.itemTitle}>Full Stack Developer at 8AM <span style={P.year}>2024 / 04 ~ Present</span></div>
                        <div style={P.projTitle}>Disaster Education Game</div>
                        <ul style={P.ul}>
                            <li style={P.li}>Designed and developed an educational game aimed at raising public awareness of disaster prevention.</li>
                        </ul>
                        <div style={P.projTitle}>Interactive AR for Disaster Education</div>
                        <ul style={P.ul}>
                            <li style={P.li}>Developed an AR-based interactive application to help users learn disaster prevention knowledge.</li>
                            <li style={P.li}>Used WebAR and Unity to create an immersive learning experience.</li>
                        </ul>
                        <div style={P.projTitle}>Outsourced Game Prototyping</div>
                        <ul style={P.ul}>
                            <li style={P.li}>Implemented gameplay feature prototypes based on producer specifications for rapid iteration and testing.</li>
                            <li style={P.li}>Built custom Unity Editor tools to empower producers to tweak and validate game parameters without engineering support.</li>
                        </ul>
                        <div style={P.projTitle}>Location-Based Mobile Game</div>
                        <ul style={P.ul}>
                            <li style={P.li}>Developed a Pokémon GO-style mobile game using Mapbox, allowing players to explore real-world maps and discover partnered store locations to earn coins and compete in tournaments.</li>
                            <li style={P.li}>Implemented map interactions including camera zoom in/out, point-of-interest discovery, and character model integration.</li>
                        </ul>
                        <PrintSkills items={['Unity','C#','AR','Mapbox','React','TypeScript','Next.js','Tailwindcss','Go','Docker','Nginx','AWS','PostgreSQL']} />
                    </div>
                    <div style={P.expBlock}>
                        <div style={P.itemTitle}>Backend Developer at CYBERBIZ <span style={P.year}>2023 / 04 ~ 2024 / 03</span></div>
                        <div style={P.projTitle}>Coordinated Backend Redesign and API Design</div>
                        <ul style={P.ul}>
                            <li style={P.li}>Collaborated with frontend developers to design and refactor the necessary APIs for the backend interface</li>
                        </ul>
                        <div style={P.projTitle}>Bug fixing and feature development according to customer requirements</div>
                        <ul style={P.ul}>
                            <li style={P.li}>Fixed bugs and developed features according to customer requirements</li>
                            <li style={P.li}>Implemented new features and fixed bugs according to customer requirements</li>
                        </ul>
                        <PrintSkills items={['Ruby on Rails','React']} />
                    </div>
                    <div style={P.expBlock}>
                        <div style={P.itemTitle}>Unity Engineer at Fourdesire <span style={P.year}>2019 / 07 ~ 2023 / 01</span></div>
                        <div style={P.projTitle}>Walkr</div>
                        <ul style={P.ul}>
                            <li style={P.li}>Refined the messaging feature to improve user experience and performance</li>
                            <li style={P.li}>Developed new features such as the spaceship system and alien visitor system</li>
                        </ul>
                        <div style={P.projTitle}>Bookmorning</div>
                        <ul style={P.ul}>
                            <li style={P.li}>Developed a mobile game using Unity and C#</li>
                            <li style={P.li}>Planned game data storage and game flow development</li>
                        </ul>
                        <div style={P.projTitle}>Ads Integration</div>
                        <ul style={P.ul}>
                            <li style={P.li}>Integrated various ad plugins into the application</li>
                            <li style={P.li}>Introduced and demonstrated ad integration to multiple product teams</li>
                        </ul>
                        <PrintSkills items={['Unity','C#','iOS','Swift','Objective-C','Xcode']} />
                    </div>
                    <div style={P.expBlock}>
                        <div style={P.itemTitle}>Android Developer at Sloth Tech <span style={P.year}>2018 / 08 ~ 2019 / 05</span></div>
                        <div style={P.projTitle}>Ported an existing iOS app to Android</div>
                        <ul style={P.ul}>
                            <li style={P.li}>Ported an existing iOS app to Android</li>
                            <li style={P.li}>Designed custom components to meet requirements due to Android UI component limitations</li>
                        </ul>
                        <div style={P.projTitle}>Created web pages for events</div>
                        <ul style={P.ul}>
                            <li style={P.li}>Created web pages for events</li>
                        </ul>
                        <PrintSkills items={['Android','Java','Kotlin','Android Studio','React','Redux']} />
                    </div>
                </div>
            </div>
        </div>
    </div>
));

const ResumePage = () => {
    const printRef = useRef(null);

    const downloadPDF = useCallback((e) => {
        e.preventDefault();
        const element = printRef.current?.firstChild;
        if (!element) return;

        html2pdf()
            .set({
                margin: 0,
                filename: 'DaYoTseng_Resume.pdf',
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff', width: 794, windowWidth: 794 },
                jsPDF: { unit: 'px', format: [794, 1123], orientation: 'portrait', hotfixes: ['px_scaling'] },
            })
            .from(element)
            .save();
    }, []);

    return (
        <StyledBackground>
            <PrintResume ref={printRef} />
            <ResumeContainer id="resume">
                <ResumeHeader>
                    <StyledHeading>Da Yo Tseng 曾大祐</StyledHeading>
                    <Subtitle>Full Stack & Game Developer</Subtitle>
                    <ContactInfo>alan25799@gmail.com</ContactInfo>
                    <HeaderSkills>
                        <SkillTag>C#</SkillTag>
                        <SkillTag>Java</SkillTag>
                        <SkillTag>JavaScript</SkillTag>
                        <SkillTag>Go</SkillTag>
                        <SkillTag>Python</SkillTag>
                        <SkillTag>Ruby</SkillTag>
                        <SkillTag>Ruby on Rails</SkillTag>
                        <SkillTag>Unity</SkillTag>
                        <SkillTag>React</SkillTag>
                        <SkillTag>Firebase</SkillTag>
                        <SkillTag>AWS</SkillTag>
                        <SkillTag>Django</SkillTag>
                        <SkillTag>Flask</SkillTag>
                        <SkillTag>PostgreSQL</SkillTag>
                        <SkillTag>TypeScript</SkillTag>
                        <SkillTag>Next.js</SkillTag>
                    </HeaderSkills>
                </ResumeHeader>
                <ResumeContent>
                    <LeftColumn>
                        <section>
                            <SectionTitle>Education</SectionTitle>
                            <ItemBlock>
                                <StyledH3>National Taiwan University <YearRange>2014 – 2016</YearRange></StyledH3>
                                <StyledParagraph>B.S. in Computer Science and Information Engineering</StyledParagraph>
                                <StyledParagraph>Coursework:</StyledParagraph>
                                <StyledUl>
                                    <StyledLi>Web Design</StyledLi>
                                    <StyledLi>Computer Graphics</StyledLi>
                                    <StyledLi>Data Structures & Algorithms</StyledLi>
                                    <StyledLi>Database Management Systems</StyledLi>
                                    <StyledLi>Game Design</StyledLi>
                                </StyledUl>
                            </ItemBlock>
                            <ItemBlock>
                                <StyledH3>National Taiwan University <YearRange>2011 – 2014</YearRange></StyledH3>
                                <StyledParagraph>Minor in Civil Engineering</StyledParagraph>
                                <StyledUl>
                                    <StyledLi>Engineering Graphics</StyledLi>
                                    <StyledLi>Application of Visualization Technology in Engineering</StyledLi>
                                </StyledUl>
                            </ItemBlock>
                        </section>
                        <section>
                            <SectionTitle>Collaborative Projects</SectionTitle>
                            <ItemBlock>
                                <StyledH3>Artist App Development <YearRange>2018 – 2019</YearRange></StyledH3>
                                <StyledDescription>
                                    Developed front-end with React.js and React Native.
                                </StyledDescription>
                                <UserSkill>
                                    <SkillTag>React.js</SkillTag>
                                    <SkillTag>React Native</SkillTag>
                                </UserSkill>
                            </ItemBlock>
                            <ItemBlock>
                                <StyledH3>CareDesign <YearRange>July 2017</YearRange></StyledH3>
                                <StyledDescription>
                                    Implemented a machine learning feature for a caregiving startup's iOS app to suggest diagnoses based on symptoms.
                                </StyledDescription>
                                <UserSkill>
                                    <SkillTag>iOS</SkillTag>
                                    <SkillTag>Swift</SkillTag>
                                    <SkillTag>Flask</SkillTag>
                                    <SkillTag>Heroku</SkillTag>
                                    <SkillTag>Python</SkillTag>
                                    <SkillTag>Keras</SkillTag>
                                    <SkillTag>Sklearn</SkillTag>
                                </UserSkill>
                            </ItemBlock>
                            <ItemBlock>
                                <StyledH3>
                                    Representative Office of the MTC in Taipei
                                    <SvgLink href="http://mtc.org.tw/new/ch/main.php" target="_blank" rel="noopener noreferrer">
                                    </SvgLink>
                                </StyledH3>
                                <YearRange>2016</YearRange>
                                <StyledDescription>
                                    A three-person project where I handled multi-language switching and Facebook integration using CouchCMS.
                                </StyledDescription>
                                <UserSkill>
                                    <SkillTag>CouchCMS</SkillTag>
                                    <SkillTag>JavaScript</SkillTag>
                                    <SkillTag>React</SkillTag>
                                </UserSkill>
                            </ItemBlock>
                            <ItemBlock>
                                <StyledH3>
                                    ParkingLotsGo
                                    <SvgLink href="https://www.youtube.com/watch?v=g7nxTQKYzw0" target="_blank" rel="noopener noreferrer">
                                    </SvgLink>
                                </StyledH3>
                                <YearRange>2016</YearRange>
                                <StyledDescription>
                                    Developed a mobile application to address parking lot issues using Swift and Xcode.
                                </StyledDescription>
                                <UserSkill>
                                    <SkillTag>iOS</SkillTag>
                                    <SkillTag>Xcode</SkillTag>
                                    <SkillTag>Swift</SkillTag>
                                </UserSkill>
                            </ItemBlock>
                            <ItemBlock>
                                <StyledH3>
                                    NTU Water Resource Education Game
                                    <SvgLink href="https://example.com/ntu-water-resource-education-game" target="_blank" rel="noopener noreferrer">
                                    </SvgLink>
                                </StyledH3>
                                <YearRange>2013</YearRange>
                                <StyledDescription>
                                    Developed educational games for a National Science Council project on water resources.
                                </StyledDescription>
                                <UserSkill>
                                    <SkillTag>Unity</SkillTag>
                                    <SkillTag>C#</SkillTag>
                                </UserSkill>
                            </ItemBlock>
                        </section>
                    </LeftColumn>

                    <RightColumn>
                        <section>
                            <SectionTitle>Work Experience</SectionTitle>
                            <ExperienceItem data-exp-item>
                                <StyledH3>Full Stack Developer at 8AM <YearRange>2024 / 04 ~ Present</YearRange></StyledH3>
                                <div>
                                    <StyledH4>
                                        Disaster Education Game
                                        <SvgLink href="https://disaster.moe.edu.tw/CITYGAME" target="_blank" rel="noopener noreferrer">
                                        </SvgLink>
                                    </StyledH4>
                                    <StyledUl>
                                        <StyledLi>Designed and developed an educational game aimed at raising public awareness of disaster prevention.</StyledLi>
                                    </StyledUl>
                                </div>
                                <div>
                                    <StyledH4>
                                        Interactive AR for Disaster Education
                                        <SvgLink href="https://skes-ar.web.app/" target="_blank" rel="noopener noreferrer"></SvgLink>
                                    </StyledH4>
                                    <StyledUl>
                                        <StyledLi>Developed an AR-based interactive application to help users learn disaster prevention knowledge.</StyledLi>
                                        <StyledLi>Used WebAR and Unity to create an immersive learning experience.</StyledLi>
                                    </StyledUl>
                                </div>
                                <div>
                                    <StyledH4>
                                        Outsourced Game Prototyping
                                    </StyledH4>
                                    <StyledUl>
                                        <StyledLi>Implemented gameplay feature prototypes based on producer specifications for rapid iteration and testing.</StyledLi>
                                        <StyledLi>Built custom Unity Editor tools to empower producers to tweak and validate game parameters without engineering support.</StyledLi>
                                    </StyledUl>
                                </div>
                                <div>
                                    <StyledH4>
                                        Location-Based Mobile Game
                                    </StyledH4>
                                    <StyledUl>
                                        <StyledLi>Developed a Pokémon GO-style mobile game using Mapbox, allowing players to explore real-world maps and discover partnered store locations to earn coins and compete in tournaments.</StyledLi>
                                        <StyledLi>Implemented map interactions including camera zoom in/out, point-of-interest discovery, and character model integration.</StyledLi>
                                    </StyledUl>
                                </div>
                                <UserSkill>
                                    <SkillTag>Unity</SkillTag>
                                    <SkillTag>C#</SkillTag>
                                    <SkillTag>AR</SkillTag>
                                    <SkillTag>Mapbox</SkillTag>
                                    <SkillTag>React</SkillTag>
                                    <SkillTag>TypeScript</SkillTag>
                                    <SkillTag>Next.js</SkillTag>
                                    <SkillTag>Tailwindcss</SkillTag>
                                    <SkillTag>Go</SkillTag>
                                    <SkillTag>Docker</SkillTag>
                                    <SkillTag>Nginx</SkillTag>
                                    <SkillTag>AWS</SkillTag>
                                    <SkillTag>PostgreSQL</SkillTag>
                                </UserSkill>
                            </ExperienceItem>
                            <ExperienceItem data-exp-item>
                                <StyledH3>Backend Developer at CYBERBIZ <YearRange>2023 / 04 ~ 2024 / 03</YearRange></StyledH3>
                                <div>
                                    <StyledH4>
                                        Coordinated Backend Redesign and API Design
                                    </StyledH4>
                                    <StyledUl>
                                        <StyledLi>Collaborated with frontend developers to design and refactor the necessary APIs for the backend interface</StyledLi>
                                    </StyledUl>
                                </div>
                                <div>
                                    <StyledH4>
                                        Bug fixing and feature development according to customer requirements
                                    </StyledH4>
                                    <StyledUl>
                                        <StyledLi>Fixed bugs and developed features according to customer requirements</StyledLi>
                                        <StyledLi>Implemented new features and fixed bugs according to customer requirements</StyledLi>
                                    </StyledUl>
                                </div>
                                <UserSkill>
                                    <SkillTag>Ruby on Rails</SkillTag>
                                    <SkillTag>React</SkillTag>
                                </UserSkill>
                            </ExperienceItem>
                            <ExperienceItem data-exp-item>
                                <StyledH3>Unity Engineer at Fourdesire <YearRange>2019 / 07 ~ 2023 / 01</YearRange></StyledH3>
                                <div>
                                    <StyledH4>
                                        Walkr
                                    </StyledH4>
                                    <StyledUl>
                                        <StyledLi>Refined the messaging feature to improve user experience and performance</StyledLi>
                                        <StyledLi>Developed new features such as the spaceship system and alien visitor system</StyledLi>
                                    </StyledUl>
                                </div>
                                <div>
                                    <StyledH4>
                                        Bookmorning
                                    </StyledH4>
                                    <StyledUl>
                                        <StyledLi>Developed a mobile game using Unity and C#</StyledLi>
                                        <StyledLi>Planned game data storage and game flow development</StyledLi>
                                    </StyledUl>
                                </div>
                                <div>
                                    <StyledH4>
                                        Ads Integration
                                    </StyledH4>
                                    <StyledUl>
                                        <StyledLi>Integrated various ad plugins into the application</StyledLi>
                                        <StyledLi>Introduced and demonstrated ad integration to multiple product teams</StyledLi>
                                    </StyledUl>
                                </div>
                                <UserSkill>
                                    <SkillTag>Unity</SkillTag>
                                    <SkillTag>C#</SkillTag>
                                    <SkillTag>iOS</SkillTag>
                                    <SkillTag>Swift</SkillTag>
                                    <SkillTag>Objective-C</SkillTag>
                                    <SkillTag>Xcode</SkillTag>
                                </UserSkill>
                            </ExperienceItem>
                            <ExperienceItem data-exp-item>
                                <StyledH3>Android Developer at Sloth Tech <YearRange>2018 / 08 ~ 2019 / 05</YearRange></StyledH3>
                                <div>
                                    <StyledH4>
                                        Ported an existing iOS app to Android
                                    </StyledH4>
                                    <StyledUl>
                                        <StyledLi>Ported an existing iOS app to Android</StyledLi>
                                        <StyledLi>Designed custom components to meet requirements due to Android UI component limitations</StyledLi>
                                    </StyledUl>
                                </div>
                                <div>
                                    <StyledH4>
                                        Created web pages for events
                                    </StyledH4>
                                    <StyledUl>
                                        <StyledLi>Created web pages for events</StyledLi>
                                    </StyledUl>
                                </div>
                                <UserSkill>
                                    <SkillTag>Android</SkillTag>
                                    <SkillTag>Java</SkillTag>
                                    <SkillTag>Kotlin</SkillTag>
                                    <SkillTag>Android Studio</SkillTag>
                                    <SkillTag>React</SkillTag>
                                    <SkillTag>Redux</SkillTag>
                                </UserSkill>
                            </ExperienceItem>
                        </section>
                    </RightColumn>
                </ResumeContent>
                <ResumeFooter>
                    <DownloadButton href="#" onClick={downloadPDF}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Download PDF
                    </DownloadButton>
                </ResumeFooter>
            </ResumeContainer>
        </StyledBackground>
    );
};

export default ResumePage;
